import React, { useState, useRef, useEffect, useCallback } from 'react';
import parsePriceToNumber from '../utils/parsePrice';
import { bookingChildren } from '../utils/bookingOccupancy';
import { verifyHotelStay } from '../utils/chatBookingDates';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../hooks/useAuth';
import { useToastContext } from '../contexts/ToastContext';
import ChatBookNowPill from './chat/ChatBookNowPill';
import { streamAIQuery, saveTrip } from '../utils/api';
import { detectIntent, formatIntentForAI } from '../utils/intentDetection';
import { trackAIChat } from '../utils/analytics';
import { trackEvent } from '../utils/tracking';
import useBookingFlow from './booking/useBookingFlow';
import CheckoutPreviewModal from './booking/CheckoutPreviewModal';
import RedirectOverlay from './booking/RedirectOverlay';
import BookingGateModals from './auth/BookingGateModals';
import TripSummaryCard from './chat/TripSummaryCard';
import MessageBubble from './chat/MessageBubble';
import ChatHeader from './chat/ChatHeader';
import ChatInput from './chat/ChatInput';
import ChatThinking from './chat/ChatThinking';
import ChatStarters from './chat/ChatStarters';
import {
  loadPersistedChat,
  clearPersistedChat,
  useChatPersistence,
} from '../hooks/useChatPersistence';

/**
 * AIChat — top-level orchestrator for the chat experience.
 *
 * Owns: messages state, sendQuery → backend, auth-gate fall-through,
 * save-trip, booking-flow modals. Persistence is delegated to
 * `useChatPersistence`. Pure rendering is delegated to:
 *   • ChatHeader / ChatInput / ChatThinking / ChatStarters
 *   • TripSummaryCard — pinned dark "concierge folder" header
 *   • MessageBubble   — individual chat bubble (text + tool cards)
 */

/**
 * Build the package-personalisation greeting shown when a user arrives
 * from "Personalise this route with AI". Renders the 3 mandatory
 * questions (hotels / dates / destinations) in a friendly, scannable
 * format. Pure string assembly — no LLM call, zero token cost.
 */
function buildPackagePersonalisationGreeting(ctx, t) {
  const cities = (ctx.destinationCities || []).join(' · ') || ctx.name;
  const extras = (ctx.optionalStops || []).join(' / ');
  const dates = (ctx.suggestedCheckIn && ctx.suggestedCheckOut)
    ? `${ctx.suggestedCheckIn} → ${ctx.suggestedCheckOut}`
    : (t('package.aiCustomizeDatesFlex') || 'flexible');
  return [
    t('package.aiCustomizeIntro').replace('{name}', ctx.name),
    '',
    t('package.aiCustomizeQ1'),
    '',
    t('package.aiCustomizeQ2').replace('{dates}', dates),
    '',
    t('package.aiCustomizeQ3').replace('{cities}', cities).replace('{extras}', extras || '—'),
    '',
    t('package.aiCustomizeClose'),
  ].join('\n');
}

export default function AIChat({ onNavigate, pageContext }) {
  const { t, language, country, nearestAirport } = useLanguage();
  const { user } = useAuth();
  const toast = useToastContext();
  const booking = useBookingFlow();

  // ===== CHAT PERSISTENCE =====
  // Save the conversation so when the user clicks Book and gets redirected
  // to Booking.com / Aviasales, then hits "back", the chat is restored
  // exactly as they left it.
  const authUserId = String(user?.user_id || user?.id || '');
  const persisted = typeof window !== 'undefined' ? loadPersistedChat(authUserId) : null;

  // Change 3 · Page-contextual welcome copy. Falls back to the generic
  // `chat.welcome` when opened outside the floating widget or on a page
  // without a dedicated intro.
  const welcomeKey = pageContext
    ? `chat.welcome.${String(pageContext).split(':', 1)[0]}`
    : 'chat.welcome';
  const welcomeText = t(welcomeKey) || t('chat.welcome');

  // Delay persistence while auth changes to avoid writing the previous
  // account's chat into the incoming account's storage namespace.
  const [hydratedUserId, setHydratedUserId] = useState(authUserId);
  const lastAccountRef = useRef(authUserId);
  const [messages, setMessages] = useState(() => {
    if (persisted?.messages?.length) return persisted.messages;
    return [{ id: '1', text: welcomeText, isUser: false, timestamp: new Date() }];
  });
  const [inputValue, setInputValue] = useState('');
  const [currentTripPlan, setCurrentTripPlan] = useState(persisted?.currentTripPlan || null);
  const [currentHotels, setCurrentHotels] = useState(persisted?.currentHotels || []);
  const [currentFlights, setCurrentFlights] = useState(persisted?.currentFlights || []);
  const [sessionId, setSessionId] = useState(persisted?.sessionId || null);
  const [loading, setLoading] = useState(false);
  const [savingTrip, setSavingTrip] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  // Intent-aware ack copy ("Crafting your Lisbon getaway for 2…") shown
  // on top of the loading bubble — emitted by the server's `meta` SSE
  // event within ~50ms. Cleared when loading flips to false.
  const [thinkingAck, setThinkingAck] = useState(null);
  // Package personalisation flow — set when user arrives via "Personalise
  // this route with AI". Used to prefix the user's first reply with the
  // package context server-side so the AI builds the right trip.
  const [packageContext, setPackageContext] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const sendQueryRef = useRef(null);
  // A synchronous ref closes the two-click race before React re-renders.
  const requestInFlightRef = useRef(false);
  const aiAbortRef = useRef(null);
  const aiGenerationRef = useRef(0);
  // Per-session rotation memory for starter-chip → /api/destinations/suggest.
  // We keep regions (max 5) + names (max 10) of destinations already
  // surfaced via chip clicks so the NEXT click rotates to a fresh
  // region. Cross-session rotation is server-side (last 5 picks in
  // MongoDB, keyed on userId).
  const sessionSkipRegions = useRef([]);
  const sessionSkipNames = useRef([]);

  useChatPersistence({
    userId: hydratedUserId === authUserId ? authUserId : null,
    messages, currentTripPlan, currentHotels, currentFlights, sessionId,
  });

  useEffect(() => {
    if (lastAccountRef.current === authUserId) return;
    lastAccountRef.current = authUserId;
    // Logout/login/second account: remove obsolete UI and abort the prior
    // account's chat stream before it can deliver more trip data.
    aiGenerationRef.current += 1;
    aiAbortRef.current?.abort();
    aiAbortRef.current = null;
    requestInFlightRef.current = false;
    setLoading(false);
    setThinkingAck(null);
    const restored = loadPersistedChat(authUserId);
    setMessages(restored?.messages?.length
      ? restored.messages
      : [{ id: '1', text: welcomeText, isUser: false, timestamp: new Date() }]);
    setCurrentTripPlan(restored?.currentTripPlan || null);
    setCurrentHotels(restored?.currentHotels || []);
    setCurrentFlights(restored?.currentFlights || []);
    setSessionId(restored?.sessionId || null);
    setHydratedUserId(authUserId);
  }, [authUserId, welcomeText]);

  // Close pending SSE when the chat disappears (navigation/unmount).
  useEffect(() => () => {
    aiGenerationRef.current += 1;
    aiAbortRef.current?.abort();
  }, []);

  // Keep the welcome message in sync with detected language + pageContext
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1) return prev;
      const only = prev[0];
      if (only?.isUser || only?.id !== '1') return prev;
      if (only.text === welcomeText) return prev;
      return [{ ...only, text: welcomeText }];
    });
  }, [language, welcomeText]);

  // After login, re-fire any pending chat query stashed before the auth gate
  useEffect(() => {
    if (!user) return;
    let pending = null;
    try {
      pending = sessionStorage.getItem('pending_chat_query');
      if (pending) sessionStorage.removeItem('pending_chat_query');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[AIChat] sessionStorage read failed:', err);
    }
    if (pending && pending.trim()) sendQueryRef.current?.(pending);
  }, [user]);

  // PACKAGE PERSONALISATION FLOW: when a package's "Personalise with AI"
  // button drops a `travelai:package:context` envelope in sessionStorage,
  // we inject a guided AI greeting bubble naming the package and asking
  // the 3 personalisation questions (hotels / dates / destinations). The
  // user can answer in plain language; their first reply gets prefixed
  // with the package context server-side so the AI builds the right trip.
  useEffect(() => {
    let raw;
    try { raw = sessionStorage.getItem('travelai:package:context'); } catch { return; }
    if (!raw) return;
    let ctx;
    try { ctx = JSON.parse(raw); } catch { return; }
    try { sessionStorage.removeItem('travelai:package:context'); } catch { /*noop*/ }
    if (!ctx?.name) return;
    setPackageContext(ctx);
    const greeting = buildPackagePersonalisationGreeting(ctx, t);
    setMessages((prev) => {
      const withoutWelcome = prev.filter((m) => m.id !== '1');
      return [
        ...withoutWelcome,
        {
          id: `pkg-intro-${Date.now()}`,
          text: greeting,
          isUser: false,
          timestamp: new Date(),
          packageContext: ctx,
        },
      ];
    });
    try { sessionStorage.removeItem('travelai:chat:prefill'); } catch {/*noop*/}
    trackEvent('package_personalise_chat_open', { package: ctx.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pick up a "Customize with AI" prompt prefilled by PackageDetailModal.
  useEffect(() => {
    try {
      const prefill = sessionStorage.getItem('travelai:chat:prefill');
      if (prefill && prefill.trim()) {
        setInputValue(prefill);
        sessionStorage.removeItem('travelai:chat:prefill');
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[AIChat] prefill read failed:', err);
    }
  }, []);

  // Pick up a "Plan this trip" preload from the Hidden Gem of the Day
  // card. Consumes both sessionStorage and a `chat:preload` window event.
  useEffect(() => {
    const consume = (raw) => {
      const prompt = (raw || '').trim();
      if (!prompt) return;
      try { sessionStorage.removeItem('chatPreload'); } catch {/*noop*/}
      setTimeout(() => sendQueryRef.current?.(prompt), 300);
    };
    try {
      const stored = sessionStorage.getItem('chatPreload');
      if (stored) consume(stored);
    } catch {
      /* private mode — ignore */
    }
    const onPreload = (e) => consume(e?.detail?.prompt);
    window.addEventListener('chat:preload', onPreload);
    return () => window.removeEventListener('chat:preload', onPreload);
  }, []);

  const handleClearChat = useCallback(() => {
    // A cleared conversation must not be repopulated by an obsolete stream.
    aiGenerationRef.current += 1;
    aiAbortRef.current?.abort();
    aiAbortRef.current = null;
    requestInFlightRef.current = false;
    setLoading(false);
    setThinkingAck(null);
    setMessages([{ id: '1', text: t('chat.welcome'), isUser: false, timestamp: new Date() }]);
    setCurrentTripPlan(null);
    setCurrentHotels([]);
    setCurrentFlights([]);
    setSessionId(null);
    clearPersistedChat(authUserId);
    trackEvent('ai_chat_cleared');
  }, [t, authUserId]);

  const handleCopy = useCallback(async (messageId, text) => {
    if (!text) return;
    const legacyCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    };
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else legacyCopy();
      setCopiedId(messageId);
      trackEvent('ai_message_copy', { messageId });
      setTimeout(() => setCopiedId((id) => (id === messageId ? null : id)), 1600);
    } catch {
      try {
        legacyCopy();
        setCopiedId(messageId);
        setTimeout(() => setCopiedId((id) => (id === messageId ? null : id)), 1600);
      } catch {
        toast?.error?.(t('chat.errorSending'));
      }
    }
  }, [toast, t]);

  // Rotating premium "thinking" messages — passed down to ChatThinking.
  const THINKING_MESSAGES = [
    t('chat.thinkingFlights'),
    t('chat.thinkingHotels'),
    t('chat.thinkingDeals'),
    t('chat.thinkingCrafting'),
  ];

  const handleSaveTrip = async () => {
    if (!user) { toast.warning('Моля, влезте в профила си, за да запазвате пътувания'); return; }
    if (!currentTripPlan) { toast.warning('Няма пътуване за запазване. Започнете планирането!'); return; }
    setSavingTrip(true);
    try {
      const result = await saveTrip({
        userId: user.id,
        tripPlan: currentTripPlan,
        hotels: currentHotels,
        flights: currentFlights,
        name: `Trip to ${currentTripPlan.destination}`,
      });
      if (result.success) toast.success('Пътуването е запазено успешно!');
      else toast.error('Неуспешно запазване на пътуването');
    } catch (error) {
      toast.error('Грешка при запазване на пътуването');
    } finally {
      setSavingTrip(false);
    }
  };

  // Hotel / flight Book handlers — used as props by MessageBubble. Hoisted
  // into AIChat so the ctx (currentTripPlan, language) stays single-source.
  const handleBookFlight = (flight) => {
    const priceNumeric = flight.priceNumeric ?? parsePriceToNumber(flight.price);
    booking.bookFlight({
      ...flight,
      priceNumeric,
      departure_at: flight.departureDate || flight.departure_at,
      return_at: flight.returnDate || flight.return_at,
      // BUG #1 FIX: pass party size so the affiliate URL builder doesn't
      // downgrade a family-of-4 trip to a 1-passenger Aviasales search.
      passengers: flight.passengers
        || flight.travelers
        || currentTripPlan?.travelers
        || (Math.max(1, Number(currentTripPlan?.adults) || 1)
          + bookingChildren(currentTripPlan?.children).count)
        || 1,
    });
  };

  const handleBookHotel = (hotel, hotelSearchContext = {}) => {
    const priceNumeric = hotel.priceNumeric ?? parsePriceToNumber(hotel.price);
    // iter95 · B1 fix — prefer the canonical destination attached by the
    // trip_consistency gate (services/trip_consistency.py). This is the
    // authoritative source; it wins over the LLM's free-text `location`
    // and any stale sessionStorage context. `cityEn` is what Booking.com
    // needs to geocode reliably.
    const destMeta = hotel.destination && typeof hotel.destination === 'object' ? hotel.destination : {};
    const canonicalCity = destMeta.canonical_name || destMeta.city || '';
    // The individual city attached to a multi-city hotel card must win
    // over the first city in the shared trip/search context.
    const cityDisplay = canonicalCity
      || hotel.city
      || hotel.location
      || hotelSearchContext.city
      || currentTripPlan?.destination
      || '';
    const cityEn = canonicalCity
      || hotel.cityEn
      || hotel.city
      || hotelSearchContext.cityEn
      || hotelSearchContext.city
      || currentTripPlan?.destinationEn
      || cityDisplay;

    // Hard-refuse the click when we truly don't know where to search.
    // Better to show a clear message than open Booking.com's "Popular
    // destinations" fallback (which routinely lands users on Rome).
    if (!cityDisplay && !cityEn) {
      toast.error(t('chat.hotelBookNoCity') || 'Не мога да отворя резервация без дестинация. Опитайте ново търсене.');
      return;
    }

    // A real hotel search must use explicit future dates. Previous fallback
    // silently fabricated a booking starting today for four nights.
    const rawCheckin = hotel.checkIn || hotelSearchContext.checkIn || currentTripPlan?.checkIn || hotel.checkin;
    const rawCheckout = hotel.checkOut || hotelSearchContext.checkOut || currentTripPlan?.checkOut || hotel.checkout;
    const stay = verifyHotelStay(rawCheckin, rawCheckout);
    if (!stay.valid) {
      toast.warning(language === 'bg'
        ? 'За да намерим правилния хотел, посочете дата на настаняване и напускане в чата.'
        : 'Please provide your check-in and check-out dates in chat before checking hotels.');
      return;
    }
    const checkin = stay.checkin;
    const checkoutDate = stay.checkout;

    // Review rating is NOT a hotel star category. Never infer a Booking.com
    // class filter from an AI-generated rating or a hotel name.

    // BUG #3 FIX: real party-size + room-count from the trip plan (we
    // compute these server-side in services/occupancy.expected_rooms now;
    // hotel.rooms is always set). Default rooms=1 only when nothing is
    // available — never override a real value.
    const childOccupancy = bookingChildren(
      currentTripPlan?.children ?? 0,
      checkin,
      currentTripPlan?.childrenBirthDates || []
    );
    const childrenCount = childOccupancy.count;
    const statedAdults = Number(currentTripPlan?.adults);
    const totalTravelers = Number(currentTripPlan?.travelers);
    const adultsCount = Math.max(1, Number.isFinite(statedAdults) && statedAdults > 0
      ? statedAdults
      : Number.isFinite(totalTravelers) && totalTravelers > childrenCount
        ? totalTravelers - childrenCount : 2);
    const roomsCount = Math.max(1, Number(hotel.rooms || hotelSearchContext.rooms || currentTripPlan?.rooms || 1));

    booking.openCheckoutPreview(
      { ...hotel, priceNumeric, stars: hotel.stars ?? hotelSearchContext.stars ?? null },
      {
        city: cityDisplay,
        cityEn,
        checkin,
        checkout: checkoutDate,
        guests: adultsCount,
        children: childrenCount,
        childrenAges: childOccupancy.ages,
        rooms: roomsCount,
        travelers: adultsCount + childrenCount,
        budgetTier: currentTripPlan?.budgetTier || null,
        language,
      },
    );
  };

  const scrollToBottom = useCallback(() => {
    const c = messagesContainerRef.current;
    if (c) c.scrollTop = c.scrollHeight;
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const sendQuery = async (queryText) => {
    if (!queryText.trim() || loading || requestInFlightRef.current) return;
    if (!user) {
      try { sessionStorage.setItem('pending_chat_query', queryText); } catch { /* ignore */ }
      booking.openAuthGate('chat_send');
      return;
    }

    requestInFlightRef.current = true;
    const generation = ++aiGenerationRef.current;
    const controller = new AbortController();
    aiAbortRef.current = controller;
    const userMessage = { id: Date.now().toString(), text: queryText, isUser: true, timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);
    setThinkingAck(null); // reset — meta event will refill within ~50ms

    // PACKAGE PERSONALISATION FLOW: if we have a packageContext from the
    // entry redirect, prefix the user's reply with a structured context
    // block so the LLM knows which package the user is personalising.
    let queryForServer = queryText;
    if (packageContext) {
      const ctx = packageContext;
      const pkgLine = [
        `[PACKAGE PERSONALISATION CONTEXT — use this verbatim, do NOT ask the user to repeat it]`,
        `Package name: ${ctx.name}`,
        `Origin city: ${ctx.origin}`,
        `Locked destinations: ${(ctx.destinationCities || []).join(', ') || ctx.name}`,
        `Optional add-on stops the user MAY pick: ${(ctx.optionalStops || []).join(', ') || 'none'}`,
        `Total nights: ${ctx.nights || 'flexible'}`,
        `Suggested check-in: ${ctx.suggestedCheckIn || 'flexible (pick next month)'}`,
        `Suggested check-out: ${ctx.suggestedCheckOut || 'flexible'}`,
        `Budget tier the user originally chose: ${ctx.budgetTier} (price from ${ctx.priceFrom || 'TBD'})`,
        '',
        'INSTRUCTION: Build the FULL personalised trip plan now using these constraints + the user message below. Do NOT ask further clarifying questions about destinations/dates/budget — those have been pre-confirmed via the personalisation chat greeting.',
        '',
        `--- USER REPLY ---`,
        queryText,
      ].join('\n');
      queryForServer = pkgLine;
      setPackageContext(null);
    }

    const intent = detectIntent(queryForServer);
    const intentContext = formatIntentForAI(intent);
    trackAIChat(queryForServer, intent.budgetType);

    try {
      const aiMessageId = (Date.now() + 1).toString();
      setMessages((prev) => [
        ...prev,
        {
          id: aiMessageId,
          text: '',
          isUser: false,
          timestamp: new Date(),
          isStreaming: true,
          streamingPhase: 'analyzing',
        },
      ]);

      const patchAiMessage = (patch) =>
        setMessages((prev) => prev.map((m) => (m.id === aiMessageId ? { ...m, ...patch } : m)));

      // Change 4 · Cross-page context — if the user picked a flight
      // deal, splice the destination + dates into the prompt so the
      // concierge doesn't ask for information the user has already
      // provided elsewhere. Uses the exact BG phrasing the ops team
      // approved so the model can quote it verbatim.
      let flightCtxSuffix = '';
      // iter97 · birth-date handoff — if HotelSearch stored a list of
      // ISO birth dates on `travelai:context`, forward them here as
      // `currentTripPlan.children:[{birthDate}]` so the backend's
      // `calculate_passenger_ages` tool runs deterministically.
      let birthDatesTripPlan = null;
      try {
        const raw = sessionStorage.getItem('travelai:context');
        if (raw) {
          const ctx = JSON.parse(raw);
          if (ctx && ctx.ts && (Date.now() - ctx.ts) < 60 * 60 * 1000 && ctx.destination_city) {
            const nights = ctx.nights ? `${ctx.nights} нощувки` : '';
            const origin = ctx.origin_city || 'София';
            flightCtxSuffix =
              `\n\n=========================================` +
              `\nКОНТЕКСТ: Потребителят е избрал полет до ${ctx.destination_city} ` +
              `(${ctx.destination_iata || ''}), ${ctx.checkin || ''} – ${ctx.checkout || ''}` +
              `${nights ? ', ' + nights : ''}, от ${origin} (${ctx.origin_iata || 'SOF'})` +
              `${ctx.guests ? `, ${ctx.guests} ${Number(ctx.guests) === 1 ? 'пътник' : 'пътници'}` : ''}.` +
              `\nНе карай потребителя да въвежда тези данни отново — ти вече ги знаеш.` +
              `\nАко това е първото ти съобщение в разговора, започни с:` +
              `\n"Виждам че сте избрали полет до ${ctx.destination_city} за ${ctx.checkin} – ${ctx.checkout}.` +
              ` Искате ли да ви помогна с хотели там, или имате друго запитване?"` +
              `\n=========================================`;
          }
          if (Array.isArray(ctx?.childrenBirthDates) && ctx.childrenBirthDates.length > 0) {
            birthDatesTripPlan = {
              adults: Number(ctx.adults) || 2,
              checkIn: ctx.checkin || undefined,
              checkOut: ctx.checkout || undefined,
              destination: ctx.destination_city || undefined,
              children: ctx.childrenBirthDates.map((d) => ({ birthDate: d })),
            };
          }
        }
      } catch { /* private mode */ }

      // Merge birth-date trip plan into the current one — never
      // overwrite existing user-set fields, only fill blanks.
      const mergedTripPlan = birthDatesTripPlan
        ? { ...(currentTripPlan || {}), ...birthDatesTripPlan,
            // preserve the LLM's destination/dates if already set
            destination: currentTripPlan?.destination || birthDatesTripPlan.destination,
            checkIn: currentTripPlan?.checkIn || birthDatesTripPlan.checkIn,
            checkOut: currentTripPlan?.checkOut || birthDatesTripPlan.checkOut,
          }
        : currentTripPlan;

      const result = await streamAIQuery(
        {
          message: queryForServer + (intentContext ? `\n\nContext: ${intentContext}` : '') + flightCtxSuffix,
          currentTripPlan: mergedTripPlan || undefined,
          userId: user?.user_id || undefined,
          sessionId,
          userOrigin: nearestAirport,
          userCountry: country,
          userLanguage: language,
          pageContext: pageContext || undefined,
        },
        Object.fromEntries(Object.entries({
          onThinking: ({ phase, sessionId: sid }) => {
            patchAiMessage({ streamingPhase: phase });
            if (sid) setSessionId(sid);
          },
          onMeta: (meta) => {
            patchAiMessage({ streamingMeta: meta });
            // Premium intent-aware ack — instant UI feedback so the user
            // never sees a generic spinner. See services/realism.build_thinking_ack
            if (meta?.thinkingAck) setThinkingAck(meta.thinkingAck);
          },
          onTripPlan: ({ tripPlan, tripWindow, message, hotelSearchContext, servedBy, sessionId: sid, fallbackMode, alternatives, insiderTips, suggestions }) => {
            patchAiMessage({
              text: message || '',
              tripPlan,
              tripWindow,
              hotelSearchContext,
              servedBy,
              fallbackMode,
              ...(alternatives ? { alternatives } : {}),
              ...(insiderTips ? { insiderTips } : {}),
              ...(suggestions ? { suggestions } : {}),
              streamingPhase: 'rendering',
            });
            if (sid) setSessionId(sid);
            if (tripPlan) setCurrentTripPlan(tripPlan);
          },
          onHotels: ({ hotels }) => {
            patchAiMessage({ hotels });
            if (Array.isArray(hotels)) setCurrentHotels(hotels);
          },
          onFlights: ({ flights }) => {
            patchAiMessage({ flights });
            if (Array.isArray(flights)) setCurrentFlights(flights);
          },
          onAlternatives: ({ alternatives }) => patchAiMessage({ alternatives }),
          onComplete: (envelope) => {
            const data = envelope?.data || {};
            patchAiMessage({
              text: data.message || '',
              tripPlan: data.tripPlan,
              tripWindow: data.tripWindow,
              hotels: data.hotels,
              flights: data.flights,
              alternatives: data.alternatives,
              flightAlternates: data.flightAlternates,
              companion: data.companion,
              groundTransport: data.groundTransport,
              dailyPlanAvailable: data.dailyPlanAvailable,
              insiderTips: data.insiderTips,
              hotelSearchContext: data.hotelSearchContext,
              suggestions: data.suggestions,
              fallbackMode: data.fallbackMode,
              isStreaming: false,
              streamingPhase: undefined,
              streamingMeta: undefined,
            });
            if (data.sessionId) setSessionId(data.sessionId);
            if (data.tripPlan) setCurrentTripPlan(data.tripPlan);
            if (Array.isArray(data.hotels)) setCurrentHotels(data.hotels);
            if (Array.isArray(data.flights)) setCurrentFlights(data.flights);
          },
          onError: ({ detail, status }) => {
            // For Cloudflare-class 5xx (520-529) or network failures (no
            // status), the `detail` is our generic English fallback. Use
            // the LOCALIZED `chat.error` so BG/EN users see the same calm
            // message in their language. For real backend errors with a
            // meaningful detail string (e.g. budget exhausted), surface
            // that — it's already user-friendly.
            const isInfra = !status || (status >= 520 && status < 530);
            patchAiMessage({
              text: isInfra ? t('chat.error') : (detail || t('chat.error')),
              isError: true,
              isStreaming: false,
              streamingPhase: undefined,
            });
          },
        }).map(([eventName, callback]) => [eventName, (...args) => {
          if (generation === aiGenerationRef.current && !controller.signal.aborted) {
            callback(...args);
          }
        }])),
        { signal: controller.signal },
      );

      // streamAIQuery already called onError on failure, updating the
      // pending bubble. Throwing added a SECOND identical error bubble.
      if (!result?.success) return;
    } catch (error) {
      if (generation === aiGenerationRef.current && !controller.signal.aborted) {
        // Keep one in-place error instead of inserting another AI message.
        setMessages((prev) => prev.map((m) => m.isStreaming && !m.isUser ? {
          ...m, text: t('chat.error'), isError: true,
          isStreaming: false, streamingPhase: undefined,
        } : m));
      }
    } finally {
      if (generation === aiGenerationRef.current) {
        aiAbortRef.current = null;
        requestInFlightRef.current = false;
        setLoading(false);
        setThinkingAck(null);
      }
    }
  };

  const handleSubmit = async (e) => { e.preventDefault(); await sendQuery(inputValue); };

  // Keep ref in sync so the post-auth re-fire effect can reach the freshest
  // version of sendQuery without listing it as a dep (which would loop).
  useEffect(() => {
    sendQueryRef.current = sendQuery;
  });

  // Action-prefixed suggestions are navigation shortcuts, not AI queries
  const ACTION_CONFIG = {
    'action:search_flights': { label: t('home.searchFlights'), target: 'flights' },
    'action:search_hotels':  { label: t('home.searchHotels'),  target: 'hotels' },
    'action:view_packages':  { label: t('home.featuredPackages'), target: 'home' },
  };

  const handleSuggestionClick = async (suggestion) => {
    const action = ACTION_CONFIG[suggestion];
    if (action) {
      // REDIRECT CONTEXT RULE: never send the user to a blank search form.
      if (currentTripPlan && (action.target === 'flights' || action.target === 'hotels')) {
        const totalTravelers = (currentTripPlan.travelers
          || (currentTripPlan.adults || 0) + (currentTripPlan.children || 0)
          || currentTripPlan.adults
          || 2);
        const fallbackCheckin = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
        const fallbackCheckout = new Date(Date.now() + 21 * 86400000).toISOString().slice(0, 10);
        const ctx = {
          city: currentTripPlan.destination || currentTripPlan.destinationEn || '',
          cityEn: currentTripPlan.destinationEn || currentTripPlan.destination || '',
          country: currentTripPlan.country || '',
          checkIn: currentTripPlan.checkIn || fallbackCheckin,
          checkOut: currentTripPlan.checkOut || fallbackCheckout,
          departureDate: currentTripPlan.checkIn || fallbackCheckin,
          returnDate: currentTripPlan.checkOut || fallbackCheckout,
          origin: currentTripPlan.origin || currentTripPlan.originIata || nearestAirport || 'SOF',
          destinationIata: currentTripPlan.destinationIata
            || currentTripPlan.iata
            || (currentFlights?.[0]?.arrival ? String(currentFlights[0].arrival).match(/\(([A-Z]{3})\)/)?.[1] : null)
            || null,
          adults: Math.max(1, Number(currentTripPlan.adults || totalTravelers || 2)),
          children: bookingChildren(currentTripPlan.children, currentTripPlan.checkIn || fallbackCheckin).count,
          rooms: currentTripPlan.rooms || 1,
          travelClass: currentTripPlan.travelClass || 'ECONOMY',
          travelers: totalTravelers,
          budgetTier: currentTripPlan.budgetTier || null,
          source: 'ai_chat_suggestion',
          savedAt: Date.now(),
        };
        try {
          sessionStorage.setItem('travelai:search:prefill', JSON.stringify(ctx));
        } catch { /* private mode — skip */ }
      }
      trackEvent('ai_suggestion_navigate', { target: action.target, hasContext: !!currentTripPlan });
      if (onNavigate) onNavigate(action.target);
      return;
    }
    await sendQuery(suggestion);
  };

  // Conversation starters (empty chat). Each starter is a [label, styleKey]
  // pair: the LABEL is shown on the button (style-only — never a destination
  // name); the STYLE KEY is passed to /api/destinations/suggest on click so
  // the backend picks an actual destination, rotating regions per-session.
  const CONVERSATION_STARTERS = [
    [t('chat.starter1'), 'hidden'],
    [t('chat.starter2'), 'family'],
    [t('chat.starter3'), 'romantic'],
    [t('chat.starter4'), 'beach'],
    [t('chat.starter5'), 'luxury'],
    [t('chat.starter6'), 'hidden'],
  ];

  /**
   * Click handler for a conversation starter chip. Resolves the chip's
   * style → a curated destination via /api/destinations/suggest, then
   * builds a concrete prompt the AI can plan around.
   */
  const handleStarterClick = async (label, styleKey) => {
    if (loading) return;
    const skipRegions = sessionSkipRegions.current.join(',');
    const skipNames = sessionSkipNames.current.join(',');
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      // Only a verified session identity belongs in account history.
      const userIdForHistory = user?.user_id || '';
      const params = new URLSearchParams({
        style: styleKey,
        sessionId: sessionId || '',
        skipRegions,
        skipNames,
        ...(userIdForHistory ? { userId: userIdForHistory } : {}),
      });
      const res = await fetch(`${apiBase}/api/destinations/suggest?${params}`, {
        credentials: 'include',
      });
      const body = await res.json();
      if (body?.success && body?.destination?.name) {
        const d = body.destination;
        sessionSkipRegions.current = [...new Set([...sessionSkipRegions.current, d.region])].slice(-5);
        sessionSkipNames.current = [...new Set([...sessionSkipNames.current, d.name])].slice(-10);
        const prompt = language === 'bg'
          ? `${label} — предложи нещо като ${d.name}, ${d.country}. Покажи ми как да стигна там и предложи хотели.`
          : `${label} — suggest something like ${d.name}, ${d.country}. Show me how to get there and suggest hotels.`;
        return sendQuery(prompt);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[AIChat] starter suggest failed:', err);
    }
    return sendQuery(label);
  };

  // Quick-action refinement chips (below AI responses with hotels/flights).
  const QUICK_ACTIONS = [
    t('chat.quickDifferentHotels'),
    t('chat.quickDifferentFlights'),
    t('chat.quickDifferentCity'),
    t('chat.quickDates'),
    t('chat.quickCheaper'),
    t('chat.quickLuxe'),
  ];

  const hasUserMessaged = messages.some((m) => m.isUser);

  return (
    <div
      className="max-w-4xl mx-auto px-4"
      data-testid="ai-chat-section"
      id="ai-chat"
    >
      <div className="bg-white rounded-2xl shadow-lg shadow-blue-900/5 border border-gray-100 overflow-hidden">
        <ChatHeader
          title={t('chat.title')}
          subtitle={t('chat.subtitle')}
          showSave={!!currentTripPlan}
          savingTrip={savingTrip}
          onSaveTrip={handleSaveTrip}
          saveLabel={t('chat.saveTrip') || 'Save Trip'}
          savingLabel={t('chat.saving') || 'Saving...'}
          liveLabel={t('chat.liveIndicator')}
        />

        {/* V5.6 · One-click "Резервирай сега" bar — visible whenever the
            visitor arrived with a fresh flight-context (destination +
            dates). Opens Booking.com search with our affiliate label
            so every conversion still earns commission. */}
        <ChatBookNowPill />

        {/* Pinned trip-summary card */}
        <TripSummaryCard
          tripPlan={currentTripPlan}
          onClear={handleClearChat}
          onJumpToBookings={() => {
            const container = messagesContainerRef.current;
            if (!container) return;
            // Scroll to the first bookable tool card (flight preferred, else hotel)
            const target = container.querySelector('[data-testid^="tool-card-flight"]')
              || container.querySelector('[data-testid^="tool-card-hotel"]')
              || container.querySelector('[data-testid^="flight-card-"]')
              || container.querySelector('[data-testid^="hotel-card-"]');
            if (target && typeof target.scrollIntoView === 'function') {
              target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        />

        {/* Messages — height adapts: compact ~340px when empty to keep the
            homepage above-the-fold, expands to 500px once the user has
            started a conversation. */}
        <div
          ref={messagesContainerRef}
          className={`${hasUserMessaged ? 'h-[500px]' : 'h-[340px] sm:h-[380px]'} overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white transition-[height] duration-300`}
        >
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              language={language}
              loading={loading}
              copiedId={copiedId}
              onCopy={handleCopy}
              onBookHotel={(hotel) => handleBookHotel(hotel, message.hotelSearchContext)}
              onBookFlight={handleBookFlight}
              onSendQuery={sendQuery}
              onSuggestionClick={handleSuggestionClick}
              actionConfig={ACTION_CONFIG}
              quickActions={QUICK_ACTIONS}
            />
          ))}

          {loading && <ChatThinking messages={THINKING_MESSAGES} primary={thinkingAck} />}

          {!hasUserMessaged && !loading && (
            <ChatStarters
              starters={CONVERSATION_STARTERS}
              title={t('chat.starterTitle')}
              showMoreLabel={t('chat.starterShowMore')}
              showLessLabel={t('chat.starterShowLess')}
              loading={loading}
              onStarterClick={handleStarterClick}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSubmit={handleSubmit}
          disabled={loading}
          placeholder={t('chat.placeholder')}
          listeningLabel={t('chat.listening')}
        />
      </div>

      {/* Affiliate Booking Flow */}
      <CheckoutPreviewModal
        isOpen={booking.modalOpen}
        onClose={booking.closeCheckoutPreview}
        hotel={booking.activeHotel}
        params={booking.activeParams}
        onConfirm={booking.confirmBooking}
      />
      <RedirectOverlay
        isVisible={booking.overlayVisible}
        pendingUrl={booking.pendingUrl}
        onDismiss={booking.dismissOverlay}
      />
      <BookingGateModals booking={booking} />
    </div>
  );
}
